// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useState, useEffect, useRef, useCallback } from 'react';
import HeroCarousel from '../components/HeroCarousel';
import SplashScreen from '../components/SplashScreen';
import GlobalStyles from '@mui/material/GlobalStyles';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  AppBar, 
  Toolbar,
  Stack,
  Link,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { styled, Theme, keyframes } from '@mui/material/styles';
import { 
  LocalShipping as LocalShippingIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ArrowForward as ArrowForwardIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Menu as MenuIcon,
  Speed as SpeedIcon,
  Public as PublicIcon,
  Inventory as InventoryIcon,
  Apartment as ApartmentIcon,
  EventAvailable as EventAvailableIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
  DirectionsCar as DirectionsCarIcon
} from '@mui/icons-material';

import { motion, AnimatePresence, useInView } from 'framer-motion';

// Import fonts
// Oswald font is loaded via Google Fonts in embedded code
// Poppins and Bebas Neue fonts are loaded via Google Fonts in embedded code

// Define color constants at the top of the file
const RED_COLOR = '#DE1F27';
const PINK_RED = '#FF2992'; 
const CARD_BG_COLOR = "rgba(0, 0, 0, 0.8)";

const GradientBackground = styled(Box)(({ theme }) => ({
  background: '#000000',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden'
}));

const ServiceCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  height: '100%',
  backgroundColor: 'rgba(0,0,0,0.5)',
  border: '1px solid rgba(255,255,255,0.1)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 20px rgba(0,0,0,0.4)',
    '& .service-title': {
      color: RED_COLOR,
    }
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '12px',
  }
}));

const CircleBackground = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '250px',
  height: '250px',
  borderRadius: '50%',
  opacity: 0.3, // Increased opacity from 0.15 to 0.3
  background: 'radial-gradient(circle, rgba(222, 31, 39, 0.8) 0%, rgba(0,0,0,0) 70%)', // Increased color intensity
  zIndex: 0,
  filter: 'blur(20px)', // Added blur effect to make it more visible
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  background: '#000000',
  padding: theme.spacing(4, 0),
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)',
  }
}));

// Add this new styled component for the decorative line
const DecorativeLine = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '2px',
  width: '100%',
  background: '#000000',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)',
  }
}));

// Add TextFade component
const TextFade = ({
  direction = 'down',
  children,
  staggerChildren = 0.1,
}: {
  direction?: 'up' | 'down';
  children: React.ReactNode;
  staggerChildren?: number;
}) => {
  const FADE_ANIMATION = {
    show: { opacity: 1, y: 0, transition: { type: 'spring', bounce: 0.3 } },
    hidden: { opacity: 0, y: direction === 'down' ? -18 : 18 },
  };
  
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? 'show' : ''}
      variants={{
        hidden: {},
        show: {
          transition: {
            staggerChildren: staggerChildren,
          },
        },
      }}
      style={{ width: '100%', textAlign: 'center' }}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child) ? (
          <motion.div variants={FADE_ANIMATION}>{child}</motion.div>
        ) : (
          child
        )
      )}
    </motion.div>
  );
};

const CarouselContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  touchAction: 'pan-y',
}));

const CarouselTrack = styled(Box)(({ theme }) => ({
  display: 'flex',
  transition: 'transform 0.5s ease-in-out',
  width: '100%',
}));

// Add mouse follower gradient effect from ServicesPage.tsx
const GradientLight = styled('div')({
  position: 'absolute',
  width: '500px',
  height: '500px',
  borderRadius: '50%',
  background: `radial-gradient(circle, ${RED_COLOR}30 0%, ${PINK_RED}15 40%, transparent 70%)`,
  filter: 'blur(60px)',
  opacity: 0.8,
  pointerEvents: 'none',
  transition: 'transform 0.15s ease-out, width 0.2s ease, height 0.2s ease',
  transform: 'translate(-50%, -50%)',
  zIndex: 0,
});

// Add this new type definition after the existing types
type ServiceItem = {
  id: string;
  icon: React.ReactNode;
  title: string;
};

// Add this new component before the LandingPage component
const MobileServiceCarousel = ({ onServiceClick }: { onServiceClick: (serviceTitle: string) => void }) => {
  const [activeSet, setActiveSet] = useState(0);
  
  const services: ServiceItem[][] = [
    [
      { id: 'car-hire', icon: <DirectionsCarIcon sx={{ color: RED_COLOR, fontSize: 24 }} />, title: 'Car Hire' },
      { id: 'parcel', icon: <LocalShippingIcon sx={{ color: RED_COLOR, fontSize: 24 }} />, title: 'Parcel Delivery' },
      { id: 'chauffeur', icon: <ApartmentIcon sx={{ color: RED_COLOR, fontSize: 24 }} />, title: 'Chauffeur' },
    ],
    [
      { id: 'transport', icon: <BusinessIcon sx={{ color: RED_COLOR, fontSize: 24 }} />, title: 'Fragile Freight' },
      { id: 'packers', icon: <InventoryIcon sx={{ color: RED_COLOR, fontSize: 24 }} />, title: 'Interstate Delivery' },
      { id: 'courier', icon: <PublicIcon sx={{ color: RED_COLOR, fontSize: 24 }} />, title: 'Door to Door Service' },
    ],
    [
      { id: 'sameday', icon: <SpeedIcon sx={{ color: RED_COLOR, fontSize: 24 }} />, title: 'Same Day Delivery' },
    ],
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSet((prev) => (prev + 1) % 3);
    }, 3000);

    return () => clearInterval(timer);
  }, []);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={activeSet}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%' }}
      >
        <Grid container spacing={2} justifyContent="center" sx={{ py: 1 }}>
          {services[activeSet].map((service, index) => (
            <Grid 
              key={service.id} 
              item 
              xs={4}
              sx={{ 
                textAlign: 'center',
                p: 0.5
              }}
            >
              <Box
                onClick={() => onServiceClick(service.title)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 1.5,
                  px: 0.5,
                  width: '100%',
                  cursor: 'pointer',
                  '&:hover': {
                    '& .service-icon-bg': {
                      bgcolor: 'rgba(222, 31, 39, 0.15)',
                    },
                    '& .service-text': {
                      color: RED_COLOR,
                    }
                  }
                }}
              >
                <Box
                  className="service-icon-bg"
                  sx={{
                    width: 42,
                    height: 42,
                    bgcolor: 'rgba(222, 31, 39, 0.08)',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 1,
                    transition: 'background-color 0.3s ease'
                  }}
                >
                  {service.icon}
                </Box>
                <Typography
                  className="service-text"
                  align="center"
                  sx={{
                    color: 'white',
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    width: '100%',
                    transition: 'color 0.3s ease',
                    mt: 0.5
                  }}
                >
                  {service.title}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        
        {/* Dots indicator */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          {[0, 1, 2].map((dot) => (
            <Box
              key={`dot-${dot}`}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                mx: 0.5,
                cursor: 'pointer',
                backgroundColor: dot === activeSet ? RED_COLOR : 'rgba(255,255,255,0.3)',
                transition: 'background-color 0.3s',
              }}
              onClick={() => setActiveSet(dot)}
            />
          ))}
        </Box>
      </motion.div>
    </AnimatePresence>
  );
};

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logisticsMenuAnchor, setLogisticsMenuAnchor] = useState<null | HTMLElement>(null);
  const [showAppBanner, setShowAppBanner] = useState(true);
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);

  const handleLogisticsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLogisticsMenuAnchor(event.currentTarget);
  };

  const handleLogisticsMenuClose = () => {
    setLogisticsMenuAnchor(null);
  };

  const handleServiceClick = (serviceMode: string) => {
    handleLogisticsMenuClose();
    navigate('/instant-quote', { state: { selectedService: serviceMode } });
    window.scrollTo(0, 0);
  };
  
  const handleAppDownload = (platform: 'apple' | 'android') => {
    window.open('https://pwa-final-101.vercel.app/', '_blank');
    setIsMobileMenuOpen(false);
    setShowAppBanner(false);
  };
  
  const closeAppBanner = () => {
    setShowAppBanner(false);
  };
  
  // Auto-dismiss banner after 15 seconds
  useEffect(() => {
    if (showAppBanner) {
      const timer = setTimeout(() => {
        setShowAppBanner(false);
      }, 15000);
      
      return () => clearTimeout(timer);
    }
  }, [showAppBanner]);
  
  // Define keyframes for animations
  const pulseAnimation = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  `;
  
  const glowAnimation = keyframes`
    0% { box-shadow: 0 0 5px rgba(222, 31, 39, 0.3); }
    50% { box-shadow: 0 0 15px rgba(222, 31, 39, 0.5); }
    100% { box-shadow: 0 0 5px rgba(222, 31, 39, 0.3); }
  `;
  
  const shimmerAnimation = keyframes`
    0% { filter: brightness(1) drop-shadow(0 2px 4px rgba(0,0,0,0.2)); }
    50% { filter: brightness(1.3) drop-shadow(0 2px 8px rgba(222, 31, 39, 0.5)); }
    100% { filter: brightness(1) drop-shadow(0 2px 4px rgba(0,0,0,0.2)); }
  `;
  
  const gradientShiftAnimation = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  `;
  
  // Function to close the app download menu when clicking outside
  const closeAppMenu = (e: MouseEvent) => {
    const appMenu = document.getElementById('app-download-menu');
    const installButton = document.getElementById('install-app-button');
    
    // Close only if clicking outside both the menu and button
    if (appMenu && 
        e.target instanceof Node && 
        !appMenu.contains(e.target) && 
        installButton && 
        !installButton.contains(e.target)) {
      appMenu.style.display = 'none';
      document.removeEventListener('click', closeAppMenu);
    }
  };

  const carouselRef = useRef<HTMLDivElement>(null);

  // Add mouse position state for gradient light effect
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isMoving, setIsMoving] = useState(false);
  const contentSectionRef = useRef<HTMLDivElement>(null);
  const movingTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Group services into slides of 3
  const slidesCount = 3; // Now 3 slides
  
  // Define the service card type
  type ServiceCard = {
    id: string;
    title: string;
    description: string;
    image: string;
  };

  // Define the slide type
  type Slide = {
    id: string;
    services: ServiceCard[];
  };
  
  // Service cards data
  const serviceCards: ServiceCard[] = [
    {
      id: 'car-hire',
      title: "Car Hire",
      description: "Professional car hire services with modern, well-maintained vehicles. Perfect for business trips, special occasions, or when you need reliable transportation.",
      image: "/car-hire-1.png",
    },
    {
      id: 'parcel-delivery',
      title: "Parcel Delivery",
      description: "Fast and secure parcel delivery solutions for businesses and individuals. We ensure your packages arrive safely and on schedule.",
      image: "/services-15.jpg",
    },
    {
      id: 'chauffeur-services',
      title: "Chauffeur Services",
      description: "Professional chauffeur services with experienced drivers. We provide reliable transportation for executives, special events, and VIP clients.",
      image: "/chauffeur-2.jpg",
    },
    {
      id: 'fragile-freight',
      title: "Fragile Freight",
      description: "Specialized handling for delicate and valuable items. Our experts use proper techniques and materials to protect your fragile shipments.",
      image: "/gallery 6.jpeg",
    },
    {
      id: 'door-to-door',
      title: "Door to Door Service",
      description: "Convenient pickup and delivery directly from your location to the destination. Let us handle the logistics while you focus on your business.",
      image: "/gallery 2.jpg",
    },
    {
      id: 'same-day-delivery',
      title: "Same Day Delivery",
      description: "Urgent deliveries handled with speed and reliability. Our same-day service ensures your time-sensitive packages reach their destination promptly.",
      image: "/gallery 3.jpg",
    },
    {
      id: 'interstate-delivery',
      title: "Interstate Delivery",
      image: "/upscalemedia-transformed.jpeg",
      description: "Seamless interstate logistics solutions connecting businesses across Australia. Our fleet ensures safe and timely delivery across state lines.",
    }
  ];
  
  // Create slides array - split services into groups of 2
  const slides: Slide[] = [
    {
      id: 'first-slide',
      services: serviceCards.slice(0, 2)
    },
    {
      id: 'second-slide',
      services: serviceCards.slice(2, 4)
    },
    {
      id: 'third-slide',
      services: serviceCards.slice(4, 6)
    }
  ];
  
  // Create desktop slides array - split services into groups of 3
  const desktopSlides: Slide[] = [
    {
      id: 'desktop-first-slide',
      services: serviceCards.slice(0, 3)
    },
    {
      id: 'desktop-second-slide',
      services: serviceCards.slice(3, 6)
    }
  ];
  
  const maxActiveSlide = slidesCount - 1;
  const desktopSlidesCount = desktopSlides.length;
  
  const handleNextSlide = useCallback(() => {
    if (isMobile) {
      setActiveSlide(prev => (prev === maxActiveSlide ? 0 : prev + 1));
    } else {
      setActiveSlide(prev => (prev === desktopSlidesCount - 1 ? 0 : prev + 1));
    }
  }, [maxActiveSlide, desktopSlidesCount, isMobile]);
  
  // Setup auto-scroll effect
  useEffect(() => {
    const interval = setInterval(() => {
      handleNextSlide();
    }, 5000); // Change slide every 5 seconds
    
    return () => clearInterval(interval);
  }, [handleNextSlide]); // Include handleNextSlide since it's used in the effect

  // Handle swipe/trackpad gestures
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    if ('touches' in e) {
      setStartX(e.touches[0].clientX);
    } else {
      setStartX(e.clientX);
      e.preventDefault();
    }
    setCurrentTranslate(activeSlide * -100);
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    
    let currentX: number;
    if ('touches' in e) {
      currentX = e.touches[0].clientX;
    } else {
      currentX = e.clientX;
    }
    
    const diff = (currentX - startX) / (carouselRef.current?.clientWidth || 1) * 100;
    const newTranslate = currentTranslate + diff;
    
    // Apply boundaries - don't allow dragging beyond first or last slide
    if (newTranslate > 0) {
      return;
    }
    
    if (newTranslate < -maxActiveSlide * 100) {
      return;
    }
    
    carouselRef.current?.style.setProperty('transform', `translateX(${newTranslate}%)`);
  };

  const handleDragEnd = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(false);
    
    // Calculate the final position to snap to
    const carouselWidth = carouselRef.current?.offsetWidth || 0;
    const pxPerSlide = carouselWidth;
    const dragged = currentTranslate - (-activeSlide * pxPerSlide);
    const threshold = pxPerSlide * 0.2;
    
    if (dragged > threshold) {
      // Dragged right enough to go to previous slide
      setActiveSlide(Math.max(0, activeSlide - 1));
    } else if (dragged < -threshold) {
      // Dragged left enough to go to next slide
      setActiveSlide(Math.min(slidesCount - 1, activeSlide + 1));
    } else {
      // Not dragged enough, snap back
      setActiveSlide(activeSlide);
    }
  };

  // Reset transition on slide change
  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.style.transition = isDragging ? 'none' : 'transform 0.5s ease-in-out';
      carouselRef.current.style.transform = `translateX(-${activeSlide * 100}%)`;
    }
  }, [activeSlide, isDragging]);

  const [showSplash, setShowSplash] = useState(() => {
    // Check if this is the first visit in the current session
    return !sessionStorage.getItem('hasVisited');
  });

  const handleSplashFinish = () => {
    setShowSplash(false);
    // Mark that the user has visited in this session
    sessionStorage.setItem('hasVisited', 'true');
  };

  // Function to handle navigation with scroll to top
  const handleNavigation = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  // Add useEffect for mouse tracking only on desktop devices
  useEffect(() => {
    // Skip mouse tracking on mobile to improve performance
    if (isMobile) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      // Use requestAnimationFrame to optimize performance
      requestAnimationFrame(() => {
        setMousePos({ 
          x: e.clientX, 
          y: e.clientY 
        });
        
        setIsMoving(true);
        
        if (movingTimeout.current) {
          clearTimeout(movingTimeout.current);
        }
        
        movingTimeout.current = setTimeout(() => {
          setIsMoving(false);
        }, 150);
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (movingTimeout.current) {
        clearTimeout(movingTimeout.current);
      }
    };
  }, [isMobile]);

  // Handle next offer slide as a no-op since we've removed activeOfferSlide
  const handleNextOfferSlide = useCallback(() => {
    // This is kept as an empty function since it's referenced elsewhere
    // but all related functionality is being removed
  }, []);

  // Setup auto-scroll effect for offer carousel with mobile optimization
  useEffect(() => {
    // Skip automatic animation on mobile to improve performance
    if (isMobile) return;
    
    const interval = setInterval(() => {
      handleNextOfferSlide();
    }, 6000); // Change slide every 6 seconds
    
    return () => clearInterval(interval);
  }, [handleNextOfferSlide, isMobile]);

  return (
    <>
      {showSplash && <SplashScreen onFinish={handleSplashFinish} />}
      <GradientBackground>
      {/* Add GlobalStyles for animations */}
      <GlobalStyles
        styles={{
          '@keyframes slideLogos': {
            '0%': {
              transform: 'translateX(0)',
            },
            '100%': {
              transform: 'translateX(-50%)',
            },
          },
          '@keyframes moveLeft': {
            '0%': { transform: 'translateX(0)' },
            '100%': { transform: 'translateX(-150px)' },
          },
          '@keyframes moveRight': {
            '0%': { transform: 'translateX(0)' },
            '100%': { transform: 'translateX(150px)' },
          },
          '@keyframes pulse': {
            '0%': { opacity: 0.15, transform: 'translate(-50%, -50%) scale(1)' },
            '50%': { opacity: 0.25, transform: 'translate(-50%, -50%) scale(1.3)' },
            '100%': { opacity: 0.15, transform: 'translate(-50%, -50%) scale(1)' },
          },
          '@keyframes buttonShine': {
            '0%': { transform: 'translateX(-100%) translateY(-100%) rotate(45deg)' },
            '20%, 100%': { transform: 'translateX(100%) translateY(100%) rotate(45deg)' },
          },
          '@keyframes shine': {
            '0%': { left: '-100%' },
            '20%, 100%': { left: '100%' }
          },
          '@keyframes ripple': {
            '0%': { transform: 'scale(0.8)', opacity: 0.3 },
            '50%': { transform: 'scale(1)', opacity: 0.5 },
            '100%': { transform: 'scale(0.8)', opacity: 0.3 },
          },
        }}
      />
      {/* App Download Banner - Only shown in mobile view */}
      {showAppBanner && isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            background: `linear-gradient(135deg, ${RED_COLOR} 0%, #9e1118 40%, #c41922 60%, ${RED_COLOR} 100%)`,
            backgroundSize: '300% 100%',
            animation: `${gradientShiftAnimation} 10s infinite ease-in-out, slideDown 0.5s ease-out`,
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(222, 31, 39, 0.4)',
            py: 1.2,
            px: { xs: 1, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '2px',
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent)',
              animation: `${glowAnimation} 3s infinite ease-in-out`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '70%', sm: '80%' }, pl: { xs: 0, sm: 1 } }}>
            <IconButton 
              onClick={closeAppBanner}
              size="small"
              sx={{ 
                color: 'white', 
                mr: 1.5,
                ml: -1.5,
                '&:hover': { color: 'rgba(255, 255, 255, 0.8)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box 
              component="img" 
              src="/MOTEX+Logo.png" 
              alt="MOTEX Logo" 
              sx={{ 
                height: 36, 
                mr: 1.5, 
                borderRadius: '8px',
                animation: `${shimmerAnimation} 4s infinite ease-in-out`,
                backgroundColor: 'black',
                padding: '4px'
              }} 
            />
            <Box sx={{ flexShrink: 1, minWidth: 0, maxWidth: { xs: 'calc(100% - 40px)', sm: '100%' } }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'white',
                  fontSize: { xs: '0.8rem', sm: '0.95rem' },
                  fontFamily: '"Poppins", sans-serif',
                  textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                Download The App
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: { xs: '0.65rem', sm: '0.8rem' },
                  fontFamily: '"Poppins", sans-serif',
                  textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                Most convenient way to schedule bookings
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            onClick={() => handleAppDownload('apple')}
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
              color: RED_COLOR,
              fontWeight: 700,
              px: { xs: 1.8, sm: 3.5 },
              py: 0.75,
              ml: { xs: 0.5, sm: 2 },
              minWidth: { xs: '80px', sm: '100px' },
              maxWidth: { xs: '85px', sm: 'none' },
              borderRadius: '50px',
              fontSize: { xs: '0.75rem', sm: '0.9rem' },
              animation: `${pulseAnimation} 2s infinite ease-in-out`,
              '&:hover': { 
                background: 'linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                animation: 'none'
              },
              fontFamily: '"Poppins", sans-serif',
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              whiteSpace: 'nowrap'
            }}
          >
            GET APP
          </Button>
        </Box>
      )}
      
      <AppBar 
        position="fixed" 
        color="transparent" 
        elevation={0} 
        sx={{ 
          py: 1, 
          backgroundColor: 'rgba(0, 0, 0, 0.85)', 
          backdropFilter: 'blur(8px)', 
          zIndex: 1100,
          top: (showAppBanner && isMobile) ? '64px' : 0,
          transition: 'top 0.3s ease-in-out'
        }}>
      
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {/* Logo on the left */}
          <Box sx={{ display: 'flex', alignItems: 'center', width: isMobile ? '50%' : '20%' }}>
            <Box 
              component="button"
              onClick={() => handleNavigation('/')}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                textDecoration: 'none',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer' 
              }}
            >
              <Box 
                component="img" 
                src="/MOTEX+Logo.png" 
                alt="MOTEX Logo" 
                sx={{ height: 28 }} 
              />
            </Box>
          </Box>
          
          {/* Desktop menu in the center */}
          {!isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '60%' }}>
              <Stack 
                direction="row" 
                spacing={2.5} 
                sx={{ 
                  color: 'white', 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400,
                  justifyContent: 'center',
                  fontSize: '15px',
                  lineHeight: '29px',
                }}
              >
                <Link 
                  component="button" 
                  onClick={() => handleNavigation('/')} 
                  color="inherit" 
                  underline="none" 
                  sx={{ 
                    color: RED_COLOR, 
                    '&:hover': { color: RED_COLOR },
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '16px',
                    lineHeight: '29px',
                    fontWeight: 400
                  }}
                >
                  Home
                </Link>
                <Link
                  component="button"
                  onClick={() => handleServiceClick('Car Hire')}
                  color="inherit"
                  underline="none"
                  sx={{
                    '&:hover': { color: RED_COLOR },
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '16px',
                    lineHeight: '29px',
                    fontWeight: 400
                  }}
                >
                  Car Hire
                </Link>
                <Link
                  component="button"
                  onClick={() => handleServiceClick('Chauffeur')}
                  color="inherit"
                  underline="none"
                  sx={{
                    '&:hover': { color: RED_COLOR },
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '16px',
                    lineHeight: '29px',
                    fontWeight: 400
                  }}
                >
                  Chauffeur
                </Link>
                <Box
                  onMouseEnter={(e) => handleLogisticsMenuOpen(e)}
                  sx={{ display: 'inline-block' }}
                >
                  <Link
                    component="button"
                    color="inherit"
                    underline="none"
                    sx={{
                      '&:hover': { color: RED_COLOR },
                      fontFamily: '"Poppins", sans-serif',
                      fontSize: '16px',
                      lineHeight: '29px',
                      fontWeight: 400
                    }}
                  >
                    Logistics Services
                  </Link>
                  <Menu
                    anchorEl={logisticsMenuAnchor}
                    open={Boolean(logisticsMenuAnchor)}
                    onClose={handleLogisticsMenuClose}
                    PaperProps={{
                      onMouseLeave: handleLogisticsMenuClose
                    }}
                    sx={{
                      '& .MuiPaper-root': {
                        bgcolor: 'rgba(0, 0, 0, 0.85)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        mt: 1
                      }
                    }}
                  >
                    <MenuItem onClick={() => handleServiceClick('Car Hire')} sx={{ color: 'white', '&:hover': { color: RED_COLOR } }}>Car Hire</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Parcel Delivery')} sx={{ color: 'white', '&:hover': { color: RED_COLOR } }}>Parcel Delivery</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Fragile Freight')} sx={{ color: 'white', '&:hover': { color: RED_COLOR } }}>Fragile Freight</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Interstate Delivery')} sx={{ color: 'white', '&:hover': { color: RED_COLOR } }}>Interstate Delivery</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Door to Door Service')} sx={{ color: 'white', '&:hover': { color: RED_COLOR } }}>Door to Door Service</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Same Day Delivery')} sx={{ color: 'white', '&:hover': { color: RED_COLOR } }}>Same Day Delivery</MenuItem>
                  </Menu>
                </Box>
                <Link 
                  component="button" 
                  onClick={() => handleNavigation('/about-us')}
                  color="inherit" 
                  underline="none" 
                  sx={{ 
                    '&:hover': { color: RED_COLOR },
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '16px',
                    lineHeight: '29px',
                    fontWeight: 400
                  }}
                >
                  About Us
                </Link>
                <Link 
                  component="button" 
                  onClick={() => handleNavigation('/instant-quote')}
                  color="inherit" 
                  underline="none" 
                  sx={{ 
                    '&:hover': { color: RED_COLOR },
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '16px',
                    lineHeight: '29px',
                    fontWeight: 400
                  }}
                >
                  Instant Quote
                </Link>
                <Link 
                  component="button" 
                  onClick={() => handleNavigation('/gallery')}
                  color="inherit" 
                  underline="none" 
                  sx={{ 
                    '&:hover': { color: RED_COLOR },
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '16px',
                    lineHeight: '29px',
                    fontWeight: 400
                  }}
                >
                  Gallery
                </Link>
                <Link 
                  component="button" 
                  onClick={() => handleNavigation('/contact-us')}
                  color="inherit" 
                  underline="none" 
                  sx={{ 
                    '&:hover': { color: RED_COLOR },
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '16px',
                    lineHeight: '29px',
                    fontWeight: 400
                  }}
                >
                  Contact
                </Link>
              </Stack>
            </Box>
          )}
          
          {/* Get A Quote button on the right and Mobile Menu */}
          <Box sx={{ display: 'flex', width: isMobile ? '50%' : '20%', justifyContent: 'flex-end', alignItems: 'center' }}>
            {/* Mobile Car Hire, Chauffeur, Logistics, and Install App buttons */}
            {isMobile && (
              <>
                <Button
                  component="button"
                  onClick={() => handleServiceClick('Car Hire')}
                  sx={{
                    color: 'white',
                    fontSize: '0.8rem',
                    mr: 1,
                    px: 1,
                    py: 0.5,
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontFamily: '"Poppins", sans-serif',
                    '&:hover': { color: RED_COLOR },
                    whiteSpace: 'nowrap'
                  }}
                >
                  Car Hire
                </Button>
                <Button
                  component="button"
                  onClick={() => handleServiceClick('Chauffeur')}
                  sx={{
                    color: 'white',
                    fontSize: '0.8rem',
                    mr: 1,
                    px: 1,
                    py: 0.5,
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontFamily: '"Poppins", sans-serif',
                    '&:hover': { color: RED_COLOR },
                    whiteSpace: 'nowrap'
                  }}
                >
                  Chauffeur
                </Button>
                <Box sx={{ position: 'relative' }}>
                  <Button
                    component="button"
                    onClick={(e) => handleLogisticsMenuOpen(e)}
                    sx={{
                      color: 'white',
                      fontSize: '0.8rem',
                      mr: 1.5,
                      px: 1,
                      py: 0.5,
                      minWidth: 'auto',
                      textTransform: 'none',
                      fontFamily: '"Poppins", sans-serif',
                      '&:hover': { color: RED_COLOR },
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Logistics
                  </Button>
                  <Menu
                    anchorEl={logisticsMenuAnchor}
                    open={Boolean(logisticsMenuAnchor)}
                    onClose={handleLogisticsMenuClose}
                    PaperProps={{
                      sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.85)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        mt: 1
                      }
                    }}
                  >
                    <MenuItem onClick={() => handleServiceClick('Car Hire')} sx={{ color: 'white', '&:hover': { color: RED_COLOR } }}>Car Hire</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Parcel Delivery')} sx={{ color: 'white', '&:hover': { color: RED_COLOR } }}>Parcel Delivery</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Fragile Freight')} sx={{ color: 'white', '&:hover': { color: RED_COLOR } }}>Fragile Freight</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Interstate Delivery')} sx={{ color: 'white', '&:hover': { color: RED_COLOR } }}>Interstate Delivery</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Door to Door Service')} sx={{ color: 'white', '&:hover': { color: RED_COLOR } }}>Door to Door Service</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Same Day Delivery')} sx={{ color: 'white', '&:hover': { color: RED_COLOR } }}>Same Day Delivery</MenuItem>
                  </Menu>
                </Box>
              </>
            )}
            
            {!isMobile && (
              <Button 
                component="button" 
                onClick={() => handleNavigation('/instant-quote')} 
                variant="contained" 
                sx={{ 
                  bgcolor: RED_COLOR, 
                  color: 'white',
                  textTransform: 'none',
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 400,
                  fontSize: '15px',
                  borderRadius: '50px',
                  px: 3,
                  py: 1,
                  minWidth: '130px',
                  whiteSpace: 'nowrap',
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    bgcolor: '#c41922',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.25)'
                  }
                }}
              >
                Get&nbsp;a&nbsp;Quote
              </Button>
            )}
            
            {/* Mobile menu icon */}
            {isMobile && (
              <IconButton
                size="large"
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={() => setIsMobileMenuOpen(true)}
                sx={{ color: 'white' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Toolbar spacer to prevent content from being hidden under fixed AppBar and banner */}
      <Box sx={{ height: showAppBanner ? '128px' : '64px', transition: 'height 0.3s ease-in-out' }} />
      
      {/* Update the Hero section to fix mobile text cropping */}
      <Box 
        sx={{ 
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          minHeight: { xs: '40.47vh', md: '55vh' },
          overflow: 'hidden',
          zIndex: 10,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            zIndex: 1
          }
        }}
      >
        <HeroCarousel 
          images={[
            '/chauffeur-4.jpg',
            '/PHOTO-2025-03-22-21-36-54_1.jpg',
            '/chauffeur-5.jpg'
            // '/chauffeur-3.jpg'
          ]}
          interval={5000}
        />
      
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, pt: 2 }}>
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ delay: 3.8, duration: 1 }}
            style={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: '40vh',
              width: '100%'
            }}
          >
            <Typography 
              variant="h2" 
              align="center" 
              sx={{ 
                mb: 1,
                fontWeight: 800,
                color: 'transparent',
                fontFamily: '"Bebas Neue", sans-serif',
                fontSize: { xs: '45px', sm: '55px', md: '85px', lg: '95px' },
                letterSpacing: { xs: '1px', sm: '2px', md: '3px' },
                WebkitTextStroke: { xs: `0.5px ${RED_COLOR}`, sm: `1.5px ${RED_COLOR}`, md: `2px ${RED_COLOR}` },
                textStroke: { xs: `0.5px ${RED_COLOR}`, sm: `1.5px ${RED_COLOR}`, md: `2px ${RED_COLOR}` },
                mt: 0
              }}
            >
              <TextFade direction="down" staggerChildren={0.03}>
                <div style={{ whiteSpace: 'nowrap' }}>
                  Above and Beyond with 
                </div>
                <div style={{ whiteSpace: 'nowrap', marginTop: '-10px' }}>
                  Motex Transport
                </div>
              </TextFade>
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Service Categories Section */}
      <Box sx={{ position: 'relative', mt: { xs: -6.5, sm: -8 }, mb: 6, zIndex: 10 }}>
        <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box 
            sx={{ 
              width: { xs: '95%', sm: '100%' },
              maxWidth: { xs: '1100px', sm: '1200px' },
              mx: 'auto',
              bgcolor: '#0A0A0A',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
              py: { xs: 1.5, sm: 1.5 },
              px: { xs: 1.5, md: 4 },
              border: '1px solid rgba(222, 31, 39, 0.15)'
            }}
          >
            {/* Mobile view with rotating services - more compact */}
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <MobileServiceCarousel onServiceClick={handleServiceClick} />
            </Box>

            {/* Desktop view with all services */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Grid container spacing={0}>
                {/* Car Hire */}
                <Grid item sm sx={{ borderRight: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <Box
                    onClick={() => handleServiceClick("Car Hire")}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 1.5,
                      px: 1.5,
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        '& .service-title': {
                          color: RED_COLOR,
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: 'rgba(222, 31, 39, 0.08)',
                        borderRadius: '10px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <DirectionsCarIcon sx={{ color: RED_COLOR, fontSize: 28 }} />
                    </Box>
                    <Typography
                      className="service-title"
                      align="center"
                      sx={{
                        color: 'white',
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      Car Hire
                    </Typography>
                  </Box>
                </Grid>

                {/* Parcel Delivery */}
                <Grid item sm sx={{ borderRight: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <Box
                    onClick={() => handleServiceClick("Parcel Delivery")}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 1.5,
                      px: 1.5,
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        '& .service-title': {
                          color: RED_COLOR,
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: 'rgba(222, 31, 39, 0.08)',
                        borderRadius: '10px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <LocalShippingIcon sx={{ color: RED_COLOR, fontSize: 28 }} />
                    </Box>
                    <Typography
                      className="service-title"
                      align="center"
                      sx={{
                        color: 'white',
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      Parcel Delivery
                    </Typography>
                  </Box>
                </Grid>

                {/* Chauffeur */}
                <Grid item sm sx={{ borderRight: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <Box
                    onClick={() => handleServiceClick("Chauffeur")}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 1.5,
                      px: 1.5,
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        '& .service-title': {
                          color: RED_COLOR,
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: 'rgba(222, 31, 39, 0.08)',
                        borderRadius: '10px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <ApartmentIcon sx={{ color: RED_COLOR, fontSize: 28 }} />
                    </Box>
                    <Typography
                      className="service-title"
                      align="center"
                      sx={{
                        color: 'white',
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      Chauffeur
                    </Typography>
                  </Box>
                </Grid>

                {/* Packers & Movers */}
                <Grid item sm sx={{ borderRight: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <Box
                    onClick={() => handleServiceClick("Interstate Delivery")}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 1.5,
                      px: 1.5,
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        '& .service-title': {
                          color: RED_COLOR,
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: 'rgba(222, 31, 39, 0.08)',
                        borderRadius: '10px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <InventoryIcon sx={{ color: RED_COLOR, fontSize: 28 }} />
                    </Box>
                    <Typography
                      className="service-title"
                      align="center"
                      sx={{
                        color: 'white',
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      Interstate Delivery
                    </Typography>
                  </Box>
                </Grid>

                {/* Courier */}
                <Grid item sm sx={{ borderRight: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <Box
                    onClick={() => handleServiceClick("Door to Door Service")}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 1.5,
                      px: 1.5,
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        '& .service-title': {
                          color: RED_COLOR,
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: 'rgba(222, 31, 39, 0.08)',
                        borderRadius: '10px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <PublicIcon sx={{ color: RED_COLOR, fontSize: 28 }} />
                    </Box>
                    <Typography
                      className="service-title"
                      align="center"
                      sx={{
                        color: 'white',
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      Door to Door Service
                    </Typography>
                  </Box>
                </Grid>

                {/* Same Day */}
                <Grid item sm sx={{ borderRight: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <Box
                    onClick={() => handleServiceClick("Same Day Delivery")}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 1.5,
                      px: 1.5,
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        '& .service-title': {
                          color: RED_COLOR,
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: 'rgba(222, 31, 39, 0.08)',
                        borderRadius: '10px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <SpeedIcon sx={{ color: RED_COLOR, fontSize: 28 }} />
                    </Box>
                    <Typography
                      className="service-title"
                      align="center"
                      sx={{
                        color: 'white',
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      Same Day Delivery
                    </Typography>
                  </Box>
                </Grid>

                {/* Fragile Freight */}
                <Grid item sm sx={{ borderRight: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <Box
                    onClick={() => handleServiceClick("Fragile Freight")}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 1.5,
                      px: 1.5,
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        '& .service-title': {
                          color: RED_COLOR,
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: 'rgba(222, 31, 39, 0.08)',
                        borderRadius: '10px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <BusinessIcon sx={{ color: RED_COLOR, fontSize: 28 }} />
                    </Box>
                    <Typography
                      className="service-title"
                      align="center"
                      sx={{
                        color: 'white',
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      Fragile Freight
                    </Typography>
                  </Box>
                </Grid>

                {/* Get a Quote button */}
                <Grid 
                  item 
                  sm="auto"
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 4
                  }}
                >
                  <Button
                    onClick={() => handleNavigation('/instant-quote')}
                    sx={{ 
                      backgroundColor: RED_COLOR, 
                      color: 'white',
                      textTransform: 'none',
                      fontFamily: '"Poppins", sans-serif',
                      fontWeight: 500,
                      fontSize: '16px',
                      borderRadius: '50px',
                      px: 3.5,
                      py: 1.25,
                      minWidth: '140px',
                      whiteSpace: 'nowrap',
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        backgroundColor: '#c41922',
                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.25)'
                      }
                    }}
                  >
                    Get&nbsp;a&nbsp;Quote
                    <Box 
                      component="span" 
                      sx={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        ml: 0.5,
                        fontSize: '1rem'
                      }}
                    >
                      →
                    </Box>
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Wrap all content except hero section in a ContentSection with mouse gradient effect */}
      <Box ref={contentSectionRef} sx={{ position: 'relative', overflow: 'hidden' }}>
        {/* Mouse follower gradient light - positioned relative to viewport */}
        <GradientLight 
          sx={{ 
            left: mousePos.x,
            top: mousePos.y,
            width: isMoving ? '600px' : '400px',
            height: isMoving ? '600px' : '400px',
            opacity: isMoving ? 0.85 : 0.7,
            position: 'fixed',
            pointerEvents: 'none',
            zIndex: 1
          }} 
        />

        {/* Services Section */}
        <Box id="services-section" sx={{ pt: -1, pb:6 , position: 'relative', zIndex: 2 }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: isMobile ? 10 : 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: isMobile ? 0.3 : 0.6 }}
            >
              <Typography variant="h2" align="center" sx={{ 
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3.5rem' },
                fontWeight: 700,
                mb: { xs: 1, md: 2 },
                color: 'white',
                fontFamily: '"Oswald", sans-serif',
              }}>
                OUR SERVICES
              </Typography>
            </motion.div>
            <Typography variant="body1" align="center" sx={{ 
              maxWidth: 700,
              mx: 'auto',
              mb: { xs: 3, md: 6 },
              color: 'rgba(255, 255, 255, 0.65)',
              fontFamily: '"Poppins", sans-serif',
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
            }}>
              From local deliveries to interstate transport, our comprehensive range of services is designed to meet all your logistics needs with efficiency and reliability.
            </Typography>
            
            <CarouselContainer>
              {/* Carousel Track */}
              <CarouselTrack 
                ref={carouselRef}
                sx={{ 
                  transform: `translateX(-${activeSlide * 100}%)`,
                }}
                onMouseDown={handleDragStart}
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
              >
                {isMobile ? 
                  // Mobile view - 2 cards per slide
                  slides.map((slide) => (
                    <Box 
                      key={slide.id} 
                      sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap',
                        width: '100%',
                        flexShrink: 0,
                        gap: 0 // Remove gap between cards
                      }}
                    >
                      {slide.services.map((card) => (
                        <Box 
                          key={card.id} 
                          sx={{ 
                            p: { xs: 1, sm: 2 }, // Add padding between cards
                            width: '50%' // Mobile always shows 2 cards (50% width)
                          }}
                        >
                          <ServiceCard onClick={() => handleServiceClick(card.title)}>
                            <CardMedia
                              component="img"
                              height={100}
                              image={card.image}
                              alt={card.title}
                              sx={{ 
                                opacity: 0.8,
                                height: { xs: 100, sm: 150 }
                              }}
                            />
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                              <Typography 
                                variant="h5" 
                                className="service-title"
                                sx={{ 
                                  mb: { xs: 0.5, sm: 1 },
                                  fontWeight: 600,
                                  color: 'white',
                                  fontFamily: '"Poppins", sans-serif',
                                  fontSize: { xs: '0.9rem', sm: '1.1rem' }
                                }}
                              >
                                {card.title}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'rgba(255,255,255,0.7)',
                                  fontFamily: '"Poppins", sans-serif',
                                  lineHeight: '1.5',
                                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                  fontWeight: 400,
                                  display: { xs: 'none', sm: 'block' }
                                }}
                              >
                                {card.description}
                              </Typography>
                            </CardContent>
                          </ServiceCard>
                        </Box>
                      ))}
                    </Box>
                  ))
                : 
                  // Desktop view - 3 cards per slide
                  desktopSlides.map((slide) => (
                    <Box 
                      key={slide.id} 
                      sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap',
                        width: '100%',
                        flexShrink: 0,
                        gap: 0 // Remove gap between cards
                      }}
                    >
                      {slide.services.map((card) => (
                        <Box 
                          key={card.id} 
                          sx={{ 
                            p: 2, // Add padding between cards
                            width: '33.333%' // Desktop shows 3 cards (33.333% width)
                          }}
                        >
                          <ServiceCard onClick={() => handleServiceClick(card.title)}>
                            <CardMedia
                              component="img"
                              height={200}
                              image={card.image}
                              alt={card.title}
                              sx={{ 
                                opacity: 0.8,
                                height: 200
                              }}
                            />
                            <CardContent sx={{ p: 3 }}>
                              <Typography 
                                variant="h5" 
                                className="service-title"
                                sx={{ 
                                  mb: 2,
                                  fontWeight: 600,
                                  color: 'white',
                                  fontFamily: '"Poppins", sans-serif',
                                  fontSize: '1.5rem'
                                }}
                              >
                                {card.title}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'rgba(255,255,255,0.7)',
                                  fontFamily: '"Poppins", sans-serif',
                                  lineHeight: '1.5',
                                  fontSize: '16px',
                                  fontWeight: 400
                                }}
                              >
                                {card.description}
                              </Typography>
                            </CardContent>
                          </ServiceCard>
                        </Box>
                      ))}
                    </Box>
                  ))
                }
              </CarouselTrack>
            </CarouselContainer>
            
            {/* Dots indicator for current slide - changes based on view */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
              {isMobile ? 
                // Mobile - 3 dots (3 slides with 2 cards each)
                [
                  { id: 'dot-first-slide', value: 0 }, 
                  { id: 'dot-second-slide', value: 1 },
                  { id: 'dot-third-slide', value: 2 }
                ].map((dot) => (
                  <Box
                    key={dot.id}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      mx: 0.5,
                      cursor: 'pointer',
                      backgroundColor: dot.value === activeSlide ? RED_COLOR : 'rgba(255,255,255,0.3)',
                      transition: 'background-color 0.3s',
                    }}
                    onClick={() => setActiveSlide(dot.value)}
                  />
                ))
              : 
                // Desktop - 2 dots (2 slides with 3 cards each)
                [
                  { id: 'dot-desktop-first-slide', value: 0 }, 
                  { id: 'dot-desktop-second-slide', value: 1 }
                ].map((dot) => (
                  <Box
                    key={dot.id}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      mx: 0.5,
                      cursor: 'pointer',
                      backgroundColor: dot.value === activeSlide ? RED_COLOR : 'rgba(255,255,255,0.3)',
                      transition: 'background-color 0.3s',
                    }}
                    onClick={() => setActiveSlide(dot.value)}
                  />
                ))
              }
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button 
                component={RouterLink}
                to="/services"
                variant="outlined" 
                endIcon={<ArrowForwardIcon />}
                sx={{ 
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  textTransform: 'none',
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 400,
                  fontSize: '15px',
                  borderRadius: '50px',
                  px: 3,
                  py: 1,
                  '&:hover': {
                    borderColor: RED_COLOR,
                    bgcolor: 'rgba(222, 31, 39, 0.1)'
                  }
                }}
              >
                View All Services
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Decorative Line */}
        <DecorativeLine />

        <LogoContainer>
          <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ 
              color: 'white',
              mb: 4, 
              letterSpacing: 1, 
              fontWeight: 800,
              fontFamily: '"Oswald", sans-serif'
            }}>
              TRUSTED BY
            </Typography>
            
            
            <Box className="logo-slide-container">
              <Box className="logo-slide">
                {[
                  { src: "/Sydney+Visitor+Centre_Small.png", alt: "Sydney Visitor Centre", id: "svc-1" },
                  { src: "/motex-transport-hunter-valley-wedding-planner.png", alt: "Hunter Valley Wedding Planner", id: "hvwp-1" },
                  { src: "/motex-transport-cwci-logo.png", alt: "CWCI", id: "cwci-1" },
                  { src: "/Sydney+Visitor+Centre_Small.png", alt: "Sydney Visitor Centre", id: "svc-2" },
                  { src: "/motex-transport-hunter-valley-wedding-planner.png", alt: "Hunter Valley Wedding Planner", id: "hvwp-2" },
                  { src: "/motex-transport-cwci-logo.png", alt: "CWCI", id: "cwci-2" }
                ].map((logo) => (
                  <Box 
                    key={logo.id}
                    component="img" 
                    src={logo.src} 
                    alt={logo.alt}
                    sx={{
                      height: '60px',
                      mx: 6,
                      opacity: 1,
                      transition: 'transform 0.3s',
                      filter: 'brightness(1)',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Container>
        </LogoContainer>

        {/* Decorative Line */}
        <DecorativeLine />

        {/* Why Choose Us Section */}
        <Box sx={{ 
          py: 10, 
          backgroundColor: '#000000',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Circular/Elliptical background patterns - increased size and added animation */}
          <CircleBackground sx={{ 
            top: '10%', 
            left: '5%',
            width: '350px', // Increased size
            height: '350px', // Increased size
            animation: 'pulse 8s infinite ease-in-out',
          }} />
          <CircleBackground sx={{ 
            top: '30%', 
            right: '8%', 
            width: '280px', // Increased size
            height: '280px', // Increased size
            background: 'radial-gradient(circle, rgba(255, 41, 146, 0.8) 0%, rgba(0,0,0,0) 70%)',
            animation: 'pulse 12s infinite ease-in-out',
          }} />
          <CircleBackground sx={{ 
            bottom: '15%', 
            left: '15%', 
            width: '400px', // Increased size
            height: '400px', // Increased size
            background: 'radial-gradient(circle, rgba(117, 57, 255, 0.7) 0%, rgba(0,0,0,0) 70%)',
            animation: 'pulse 10s infinite ease-in-out',
          }} />
          <CircleBackground sx={{ 
            bottom: '25%', 
            right: '15%', 
            width: '320px', // Increased size
            height: '320px', // Increased size
            animation: 'pulse 14s infinite ease-in-out',
          }} />
          
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h2" align="center" sx={{ 
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3.5rem' },
              fontWeight: 700,
              mb: { xs: 1, md: 2 },
              color: 'white',
              fontFamily: '"Oswald", sans-serif',
            }}>
              WHAT WE OFFER
            </Typography>
            
            <Typography variant="body1" align="center" sx={{ 
              maxWidth: 700,
              mx: 'auto',
              mb: { xs: 3, md: 6 },
              color: 'rgba(255, 255, 255, 0.65)',
              fontFamily: '"Poppins", sans-serif',
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
            }}>
              MOTEX Transport provides comprehensive logistics solutions designed to meet your specific business needs with reliability and efficiency.
            </Typography>
            
            <Grid 
              container 
              spacing={{ xs: 2, md: 3 }} 
              justifyContent="center"
              alignItems="stretch"
            >
              {/* Dedicated Drivers */}
              <Grid item xs={6} md={4}>
                <Card
                  sx={{
                    background: CARD_BG_COLOR,
                    height: "100%",
                    borderRadius: "16px",
                    p: { xs: 2, md: 3 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    cursor: "pointer",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      "& .section-heading": {
                        color: RED_COLOR
                      }
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1, md: 2 } }}>
                    <Typography 
                      variant="h4"
                      className="section-heading" 
                      sx={{ 
                        mb: { xs: 1, md: 2 },
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 700,
                        color: 'white',
                        transition: 'color 0.3s ease',
                        fontSize: { xs: '1.25rem', md: '1.5rem' }
                      }}
                    >
                      Dedicated Drivers
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        mb: { xs: 2, md: 3 },
                        color: 'rgba(255, 255, 255, 0.65)',
                        fontFamily: '"Poppins", sans-serif',
                        minHeight: { xs: '40px', md: '60px' },
                        fontSize: { xs: '0.85rem', md: '1rem' }
                      }}
                    >
                      Professional drivers who meet your service standards.
                    </Typography>
                    
                    <Divider sx={{ my: { xs: 1.5, md: 3 }, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    
                    <Box sx={{ mb: 3, display: { xs: 'none', sm: 'block' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#DE1F27', 
                            mr: 1.5,
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: { xs: '0.8rem', md: '1rem' }
                        }}>
                          Customer service focused
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#DE1F27', 
                            mr: 1.5,
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: { xs: '0.8rem', md: '1rem' }
                        }}>
                          Strong communication skills
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#DE1F27', 
                            mr: 1.5,
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: { xs: '0.8rem', md: '1rem' }
                        }}>
                          Professional presentation
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#DE1F27', 
                            mr: 1.5,
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: { xs: '0.8rem', md: '1rem' }
                        }}>
                          Timely deliveries guaranteed
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Skilled Equipment Operators */}
              <Grid item xs={6} md={4}>
                <Card
                  sx={{
                    background: CARD_BG_COLOR,
                    height: "100%",
                    borderRadius: "16px",
                    p: { xs: 2, md: 3 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    cursor: "pointer",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      "& .section-heading": {
                        color: RED_COLOR
                      }
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1, md: 2 } }}>
                    <Typography 
                      variant="h4"
                      className="section-heading" 
                      sx={{ 
                        mb: { xs: 1, md: 2 },
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 700,
                        color: 'white',
                        transition: 'color 0.3s ease',
                        fontSize: { xs: '1.25rem', md: '1.5rem' }
                      }}
                    >
                      Skilled Operators
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      mb: { xs: 2, md: 3 },
                      color: 'rgba(255, 255, 255, 0.65)',
                      fontFamily: '"Poppins", sans-serif',
                      minHeight: { xs: '40px', md: '60px' },
                      fontSize: { xs: '0.85rem', md: '1rem' }
                    }}>
                      Equipped with forklift licenses and proper tools.
                    </Typography>
                    
                    <Divider sx={{ my: { xs: 1.5, md: 3 }, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    
                    <Box sx={{ mb: 3, display: { xs: 'none', sm: 'block' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#DE1F27', 
                            mr: 1.5,
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: { xs: '0.8rem', md: '1rem' }
                        }}>
                          Certified forklift operators
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#DE1F27', 
                            mr: 1.5,
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: { xs: '0.8rem', md: '1rem' }
                        }}>
                          Proper equipment for all jobs
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#DE1F27', 
                            mr: 1.5,
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: { xs: '0.8rem', md: '1rem' }
                        }}>
                          Versatile cargo handling
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#DE1F27', 
                            mr: 1.5,
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: { xs: '0.8rem', md: '1rem' }
                        }}>
                          Self-sufficient delivery process
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Modern Fleet */}
              <Grid 
                item 
                xs={6} 
                md={4} 
                sx={{ 
                  ml: { xs: 'auto', sm: 0 },
                  mr: { xs: 'auto', sm: 0 },
                  maxWidth: { xs: 'calc(50% - 16px)', sm: 'none' }
                }}
              >
                <Card
                  sx={{
                    background: CARD_BG_COLOR,
                    height: "100%",
                    borderRadius: "16px",
                    p: { xs: 2, md: 3 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    cursor: "pointer",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      "& .section-heading": {
                        color: RED_COLOR
                      }
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1, md: 2 } }}>
                    <Typography 
                      variant="h4"
                      className="section-heading" 
                      sx={{ 
                        mb: { xs: 1, md: 2 },
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 700,
                        color: 'white',
                        transition: 'color 0.3s ease',
                        fontSize: { xs: '1.25rem', md: '1.5rem' }
                      }}
                    >
                      Modern Fleet
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      mb: { xs: 2, md: 3 },
                      color: 'rgba(255, 255, 255, 0.65)',
                      fontFamily: '"Poppins", sans-serif',
                      minHeight: { xs: '40px', md: '60px' },
                      fontSize: { xs: '0.85rem', md: '1rem' }
                    }}>
                      Current model trucks and Mercedes Benz vehicles.
                    </Typography>
                    
                    <Divider sx={{ my: { xs: 1.5, md: 3 }, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    
                    <Box sx={{ mb: 3, display: { xs: 'none', sm: 'block' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#DE1F27', 
                            mr: 1.5,
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: { xs: '0.8rem', md: '1rem' }
                        }}>
                          Latest model trucks
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#DE1F27', 
                            mr: 1.5,
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: { xs: '0.8rem', md: '1rem' }
                        }}>
                          Mercedes Benz Sprinters
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#DE1F27', 
                            mr: 1.5,
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: { xs: '0.8rem', md: '1rem' }
                        }}>
                          Mercedes Benz Vito vans
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#DE1F27', 
                            mr: 1.5,
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: { xs: '0.8rem', md: '1rem' }
                        }}>
                          Regularly maintained
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Decorative Line */}
        <DecorativeLine />

        <Box 
          sx={{ 
            py: { xs: 10, md: 14 },
            position: 'relative',
            background: 'linear-gradient(135deg, #000000 0%, #1a0005 100%)',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {/* Simple accent background */}
          <Box 
            sx={{
              position: 'absolute',
              top: '-10%',
              right: '-5%',
              width: '400px',
              height: '400px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(222, 31, 39, 0.2) 0%, rgba(0,0,0,0) 70%)',
              filter: 'blur(60px)',
              zIndex: 1,
            }}
          />

          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
            <Grid 
              container 
              spacing={{ xs: 6, md: 8 }} 
              alignItems="center" 
              sx={{ position: 'relative' }}
            >
              {/* Left side with content */}
              <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
                <motion.div
                  initial={{ opacity: 0, y: isMobile ? 10 : 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: isMobile ? 0.3 : 0.6 }}
                >
                  <Box sx={{ 
                    p: { xs: 3, md: 5 },
                    borderRadius: '16px',
                    background: 'rgba(15, 15, 15, 0.6)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 15px 30px rgba(0, 0, 0, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                  }}>
                    <Box>
                      
                      <Typography variant="h2" sx={{ 
                        fontSize: { xs: '1.75rem', sm: '2rem', md: '3rem' },
                        fontWeight: 800,
                        mb: 2,
                        color: '#FFFFFF',
                        fontFamily: '"Oswald", sans-serif',
                        lineHeight: 1.2,
                        wordBreak: { xs: 'break-word', sm: 'normal' },
                        letterSpacing: { xs: '-0.5px', md: 'normal' }
                      }}>
                        GET YOUR TRANSPORT QUOTE IN SECONDS
                      </Typography>
                      
                      <Typography variant="body1" sx={{ 
                        mb: { xs: 2, md: 4 },
                        color: 'rgba(255, 255, 255, 0.65)',
                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                        lineHeight: 1.5,
                        fontFamily: '"Poppins", sans-serif',
                      }}>
                        Skip the waiting and paperwork. Our instant quote system delivers 
                        quotes instantly so you can book your shipment and get moving.
                      </Typography>
                      
                      {/* Features */}
                      <Box sx={{ mb: { xs: 2, md: 4 } }}>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <InventoryIcon sx={{ fontSize: { xs: 20, md: 25 }, color: RED_COLOR, mr: 2 }} />
                          <Typography variant="body2" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
                            All package sizes and weights accommodated
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <EventAvailableIcon sx={{ fontSize: { xs: 20, md: 25 }, color: RED_COLOR, mr: 2 }} />
                          <Typography variant="body2" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
                            Same-day and scheduled deliveries available
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Button
                        variant="contained" 
                        onClick={() => handleNavigation('/instant-quote')}
                        sx={{ 
                          bgcolor: RED_COLOR, 
                          color: 'white',
                          textTransform: 'none',
                          fontFamily: '"Poppins", sans-serif',
                          fontWeight: 400,
                          fontSize: '15px',
                          borderRadius: '50px',
                          padding: '8px 24px',
                          minWidth: '130px',
                          whiteSpace: 'nowrap',
                          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                          position: 'relative',
                          overflow: 'hidden',
                          '&:hover': {
                            bgcolor: '#c41922',
                            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.25)'
                          }
                        }}
                      >
                        Get&nbsp;a&nbsp;Quote
                      </Button>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
              
              {/* Right side with image */}
              <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Box 
                    sx={{ 
                      position: 'relative',
                      height: { xs: '300px', md: '500px' },
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                    }}
                  >
                    <Box 
                      component="img"
                      loading="lazy"
                      src="/PHOTO-2025-03-22-21-36-55.jpg"
                      alt="Get Instant Quote"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.4s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.05)'
                        }
                      }}
                    />
                    
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                      height: '40%',
                      zIndex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      p: 3
                    }}>
                      
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Decorative Line */}
        <DecorativeLine />

        <Box sx={{ bgcolor: RED_COLOR, py: 6 }}>
          <Container maxWidth="lg">
            <Grid container spacing={4}>
              <Grid item xs={12} md={5}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box 
                    component="img" 
                    src="/MOTEX+Logo.png" 
                    alt="MOTEX Logo" 
                    sx={{ 
                      height: 40, 
                      filter: 'brightness(0) invert(1)'
                    }} 
                  />
                </Box>
                <Typography variant="body2" sx={{ color: 'white', opacity: 0.9, mb: 3, fontFamily: '"Poppins", sans-serif', fontWeight: 300 }}>
                  MOTEX Transport is a leading provider of logistics and transportation services across Australia, offering reliable and efficient solutions for businesses of all sizes.
                </Typography>
                
                {/* Social Media Icons */}
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <IconButton 
                    sx={{ 
                      color: 'white', 
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.3)' }
                    }}
                    component="a"
                    href="https://www.instagram.com/motextransport/"
                  >
                    <InstagramIcon />
                  </IconButton>
                </Stack>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h6" sx={{ color: 'white', mb: 2, fontFamily: '"Poppins", sans-serif', fontWeight: 'bold', fontSize: '20px' }}>
                  Quick Links
                </Typography>
                <Box sx={{ display: { xs: 'grid', md: 'flex' }, gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'none' }, gap: { xs: 1, md: 0 }, flexDirection: { md: 'column' } }}>
                  <Link href="/" color="inherit" underline="hover" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontWeight: 300, mb: { xs: 0, md: 1 } }}>
                    Home
                  </Link>
                  <Link href="/about-us" color="inherit" underline="hover" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontWeight: 300, mb: { xs: 0, md: 1 } }}>
                    About Us
                  </Link>
                  <Link href="#" color="inherit" underline="hover" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontWeight: 300, mb: { xs: 0, md: 1 } }}>
                    Services
                  </Link>
                  <Link href="/instant-quote" color="inherit" underline="hover" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontWeight: 300, mb: { xs: 0, md: 1 } }}>
                    Instant Quote
                  </Link>
                  <Link href="/gallery" color="inherit" underline="hover" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontWeight: 300, mb: { xs: 0, md: 1 } }}>
                    Gallery
                  </Link>
                  <Link href="#" color="inherit" underline="hover" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontWeight: 300, mb: { xs: 0, md: 0 } }}>
                    Contact
                  </Link>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="h6" sx={{ color: 'white', mb: 2, fontFamily: '"Poppins", sans-serif', fontWeight: 'bold', fontSize: '20px' }}>
                  Contact Information
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationIcon sx={{ color: 'white', mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.9, fontFamily: '"Poppins", sans-serif', fontWeight: 300 }}>
                  Rozelle NSW 2039, Australia
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon sx={{ color: 'white', mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.9, fontFamily: '"Poppins", sans-serif', fontWeight: 300 }}>
                    +61 423 440 056
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon sx={{ color: 'white', mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.8, fontFamily: '"Poppins", sans-serif', fontWeight: 300 }}>
                  roy@motextransport.com.au
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.3)', my: 4 }} />
            
            <Typography variant="body2" align="center" sx={{ color: 'white', opacity: 0.9, fontFamily: '"Poppins", sans-serif', fontWeight: 300 }}>
              © {new Date().getFullYear()} MOTEX Transport. All rights reserved.
            </Typography>
          </Container>
        </Box>
      </Box>
      
      {/* Mobile Menu */}
      <Drawer
        anchor="right"
        open={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 300 },
            backgroundColor: '#050505',
            padding: { xs: 2, sm: 3 }
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton 
            onClick={() => setIsMobileMenuOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Box component="img" src="/MOTEX+Logo.png" alt="MOTEX Logo" sx={{ width: 120, my: 2 }} />
        
        <List>
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => {
                handleNavigation('/');
                setIsMobileMenuOpen(false);
              }}
              sx={{ 
                py: 1.5,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)' }
              }}
            >
              <ListItemText 
                primary="Home" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 600, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => {
                handleNavigation('/services');
                setIsMobileMenuOpen(false);
              }}
              sx={{ 
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              <ListItemText 
                primary="Services" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => {
                handleNavigation('/about-us');
                setIsMobileMenuOpen(false);
              }}
              sx={{ 
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              <ListItemText 
                primary="About Us" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => {
                handleNavigation('/instant-quote');
                setIsMobileMenuOpen(false);
              }}
              sx={{ 
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              <ListItemText 
                primary="Instant Quote" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => {
                handleNavigation('/gallery');
                setIsMobileMenuOpen(false);
              }}
              sx={{ 
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              <ListItemText 
                primary="Gallery" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => {
                handleNavigation('/contact-us');
                setIsMobileMenuOpen(false);
              }}
              sx={{ 
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              <ListItemText 
                primary="Contact Us" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          {/* Install the App option in hamburger menu */}
          <ListItem disablePadding>
            <ListItemButton 
              sx={{ 
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              <ListItemText 
                primary="Install the App" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          {/* App Download Options - Side by Side */}
          <Box sx={{ pl: 1.98, pr: 2, mb: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={() => handleAppDownload('apple')}
              sx={{
                color: 'white',
                textTransform: 'none',
                py: 1.5,
                width: '48%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 500,
                fontSize: '0.9rem',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                '&:hover': { 
                  backgroundColor: 'rgba(222, 31, 39, 0.15)',
                  color: RED_COLOR 
                }
              }}
            >
              <AppleIcon sx={{ fontSize: '1.5rem', mb: 0.3 }} />
              Apple
            </Button>
            <Button
              onClick={() => handleAppDownload('android')}
              sx={{
                color: 'white',
                textTransform: 'none',
                py: 1.5,
                width: '48%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 500,
                fontSize: '0.9rem',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                '&:hover': { 
                  backgroundColor: 'rgba(222, 31, 39, 0.15)',
                  color: RED_COLOR 
                }
              }}
            >
              <AndroidIcon sx={{ fontSize: '1.5rem', mb: 0.3 }} />
              Android
            </Button>
          </Box>

        </List>
        
        <Box sx={{ p: 2, mt: 2 }}>
          <Button 
            onClick={() => {
              handleNavigation('/instant-quote');
              setIsMobileMenuOpen(false);
            }}
            variant="contained" 
            fullWidth
            sx={{
              backgroundColor: RED_COLOR,
              color: 'white',
              fontFamily: '"Oswald", sans-serif',
              fontWeight: 500,
              py: 1,
              borderRadius: 1,
              transition: '0.3s',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '&:hover': {
                backgroundColor: '#c50000',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
              }
            }}
          >
            Get a Quote
          </Button>
        </Box>
      </Drawer>
    </GradientBackground>
    </>
  );
};

export default LandingPage;